/** The signed-in frame: PanelShell with a menu built from installed module manifests. */

import { useMemo } from "react";
import { Link, Outlet, useLocation, useNavigate } from "react-router";

import { LANGUAGES, useTranslation } from "@motolynk/i18n";
import {
  Button,
  Icon,
  LanguageSwitcher,
  Logo,
  PanelShell,
  UserMenu,
  type NavGroup,
  type UserMenuItem,
} from "@motolynk/ui";

import { useAppConfig } from "./config";
import { isFeatureOn } from "./features";
import type { ModuleNavItem } from "./module";
import { useLogout, usePanel, usePermissions } from "./session";
import { useTheme } from "./theme";

export function isActivePath(pathname: string, to: string): boolean {
  if (to === "/") return pathname === "/";
  return pathname === to || pathname.startsWith(`${to}/`);
}

export function PanelLayout() {
  const { t, i18n } = useTranslation();
  const config = useAppConfig();
  const { panel, modules, installed, me, features } = usePanel();
  const { can, roles, isSuperuser } = usePermissions();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const logout = useLogout();
  const { theme, toggle } = useTheme();

  const navGroups = useMemo<NavGroup[]>(() => {
    const items: ModuleNavItem[] = modules
      .filter((m) => installed.has(m.name))
      .flatMap((m) => m[panel]?.nav ?? [])
      .filter((item) => can(item.permission) && isFeatureOn(features, item.feature));
    // "/settings/features" also starts with "/settings": only the longest matching entry is active.
    const activeTo = items
      .filter((item) => isActivePath(pathname, item.to))
      .reduce<string | null>(
        (best, item) => (!best || item.to.length > best.length ? item.to : best),
        null,
      );
    return config.navGroups
      .map((group) => ({
        id: group.id,
        label: group.label ? t(group.label) : undefined,
        items: items
          .filter((item) => item.group === group.id)
          .sort((a, b) => (a.order ?? 100) - (b.order ?? 100))
          .map((item) => ({
            id: item.id,
            label: t(item.label),
            icon: item.icon,
            href: item.to,
            active: item.to === activeTo,
          })),
      }))
      .filter((group) => group.items.length > 0);
  }, [modules, installed, panel, can, features, config.navGroups, pathname, t]);

  const topbar = useMemo(
    () =>
      modules
        .filter((m) => installed.has(m.name))
        .flatMap((m) => m[panel]?.topbar ?? [])
        .sort((a, b) => (a.order ?? 100) - (b.order ?? 100)),
    [modules, installed, panel],
  );

  const userMenuItems: UserMenuItem[] = [
    ...modules
      .filter((m) => installed.has(m.name))
      .flatMap((m) => m[panel]?.userMenu ?? [])
      .map((item) => ({
        id: item.id,
        label: t(item.label),
        icon: item.icon,
        onSelect: () => navigate(item.to),
      })),
    {
      id: "theme",
      label: theme === "dark" ? t("app.lightMode") : t("app.darkMode"),
      icon: theme === "dark" ? "sun" : "moon",
      onSelect: toggle,
    },
    {
      id: "logout",
      label: t("actions.logout"),
      icon: "logout",
      tone: "danger",
      onSelect: () => logout.mutate(),
    },
  ];

  const caption = isSuperuser
    ? "Super Admin"
    : (roles[0] ?? (panel === "vendor" ? t("nav.groups.store") : undefined));

  return (
    <PanelShell
      brand={
        <Link to={config.homePath ?? "/"} aria-label="MOTOLYNK">
          <Logo tone="light" product={config.product} />
        </Link>
      }
      brandCollapsed={<Logo variant="mark" tone="light" />}
      navGroups={navGroups}
      renderLink={({ href, children, ...rest }) => (
        <Link to={href} {...rest}>
          {children}
        </Link>
      )}
      storageKey={`${config.storageKey ?? `motolynk.${panel}`}.sidebar`}
      labels={{
        openMenu: t("app.openMenu"),
        collapse: t("app.collapseSidebar"),
        expand: t("app.expandSidebar"),
        skipToContent: t("app.skipToContent"),
        navigation: t("app.navigation"),
      }}
      topbarEnd={
        <>
          <LanguageSwitcher
            value={i18n.language}
            languages={LANGUAGES}
            label={t("app.language")}
            onChange={(code) => void i18n.changeLanguage(code)}
          />
          <Button
            variant="ghost"
            size="icon"
            onClick={toggle}
            aria-label={theme === "dark" ? t("app.lightMode") : t("app.darkMode")}
            className="hidden sm:inline-flex"
          >
            <Icon name={theme === "dark" ? "sun" : "moon"} />
          </Button>
          {topbar.map(({ id, component: Widget }) => (
            <Widget key={id} />
          ))}
          <UserMenu
            name={me.user.full_name || me.user.first_name || ""}
            email={me.user.email}
            caption={caption}
            items={userMenuItems}
          />
        </>
      }
    >
      <Outlet />
    </PanelShell>
  );
}
